import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { DBNotification } from "../types";
import { HISTORY_KEY, INBOX_KEY } from "../queryKeys";

/**
 * Bundle of every DB-write mutation used against the notifications table
 * directly (i.e. excluding invite-specific edge functions / RPCs).
 *
 * All six mutations follow the same optimistic-update recipe:
 *
 *   1. cancelQueries on the affected cache key (`INBOX_KEY` or `HISTORY_KEY`)
 *   2. snapshot prev cache value
 *   3. write the optimistic patch synchronously
 *   4. on success → invalidate both keys (keeps them in sync)
 *   5. on error   → restore prev snapshot + destructive toast
 *      (success paths stay silent so feedback stays in the bell list.)
 *
 * Delete is implemented as a "soft delete" (flipping `is_archived=true`
 * + `is_read=true`) so nothing is ever permanently destroyed from the
 * client — the row just leaves the visible surface.
 */
export function useNotifMutations(inbox: DBNotification[]) {
  const queryClient = useQueryClient();

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("id", id);
      if (error) throw error;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: INBOX_KEY });
      const prev = queryClient.getQueryData<DBNotification[]>(INBOX_KEY);
      queryClient.setQueryData<DBNotification[]>(INBOX_KEY, (old = []) =>
        old.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      );
      return { prev };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(INBOX_KEY, ctx.prev);
      toast({ title: "Failed to mark read", variant: "destructive" });
    },
  });

  const archive = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true, is_archived: true })
        .eq("id", id);
      if (error) throw error;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: INBOX_KEY });
      const prev = queryClient.getQueryData<DBNotification[]>(INBOX_KEY);
      queryClient.setQueryData<DBNotification[]>(INBOX_KEY, (old = []) =>
        old.filter((n) => n.id !== id),
      );
      return { prev };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(INBOX_KEY, ctx.prev);
      toast({ title: "Failed to archive", variant: "destructive" });
    },
  });

  const unarchive = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ is_archived: false })
        .eq("id", id);
      if (error) throw error;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: HISTORY_KEY });
      const prev = queryClient.getQueryData<DBNotification[]>(HISTORY_KEY);
      queryClient.setQueryData<DBNotification[]>(HISTORY_KEY, (old = []) =>
        old.filter((n) => n.id !== id),
      );
      return { prev };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(HISTORY_KEY, ctx.prev);
      toast({ title: "Failed to restore", variant: "destructive" });
    },
  });

  const deleteOne = useMutation({
    mutationFn: async ({
      id,
      fromHistory,
    }: {
      id: string;
      fromHistory: boolean;
    }) => {
      // Soft delete: update is_archived to true
      const { error } = await supabase
        .from("notifications")
        .update({ is_archived: true, is_read: true })
        .eq("id", id);
      if (error) throw error;
      return fromHistory;
    },
    onMutate: async ({ id, fromHistory }) => {
      const key = fromHistory ? HISTORY_KEY : INBOX_KEY;
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<DBNotification[]>(key);
      queryClient.setQueryData<DBNotification[]>(key, (old = []) =>
        old.filter((n) => n.id !== id),
      );
      return { prev, key };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(ctx.key, ctx.prev);
      toast({ title: "Failed to delete", variant: "destructive" });
    },
  });

  const clearAllInbox = useMutation({
    mutationFn: async () => {
      const ids = inbox.map((n) => n.id);
      if (!ids.length) return;
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true, is_archived: true })
        .in("id", ids);
      if (error) throw error;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: INBOX_KEY });
      const prev = queryClient.getQueryData<DBNotification[]>(INBOX_KEY);
      queryClient.setQueryData<DBNotification[]>(INBOX_KEY, []);
      return { prev };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
      // Success is silent: inbox empties in-place; no floating toast.
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(INBOX_KEY, ctx.prev);
      toast({ title: "Failed to clear inbox", variant: "destructive" });
    },
  });

  const bulkDelete = useMutation({
    mutationFn: async (ids: string[]) => {
      if (!ids.length) return;
      // Soft delete for the whole batch
      const { error } = await supabase
        .from("notifications")
        .update({ is_archived: true, is_read: true })
        .in("id", ids);
      if (error) throw error;
    },
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: HISTORY_KEY });
      const prev  = queryClient.getQueryData<DBNotification[]>(HISTORY_KEY);
      const idSet = new Set(ids);
      queryClient.setQueryData<DBNotification[]>(HISTORY_KEY, (old = []) =>
        old.filter((n) => !idSet.has(n.id)),
      );
      return { prev };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
      // Success is silent: history list updates; no floating toast.
    },
    onError: (_e, _ids, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(HISTORY_KEY, ctx.prev);
      toast({ title: "Bulk operation failed", variant: "destructive" });
    },
  });

  return { markRead, archive, unarchive, deleteOne, clearAllInbox, bulkDelete };
}
